/**
 * Fairy Farm Demo - Whimsical Computation Graph Demo
 *
 * A fantasy-themed demo showing the computation graph with fairy dust farming.
 */

import React from 'react';
import {
  GraphProvider,
  V,
  InspectorPanel,
  OverrideBanner,
  useOverrides,
} from '../lib/computation-graph';
import { fairyDustFarmGraph } from '../graphs/fairy-dust-farm';

/**
 * Reset button component
 */
function ResetButton() {
  const { hasOverrides, clearAllOverrides } = useOverrides();

  if (!hasOverrides) return null;

  return (
    <button
      onClick={clearAllOverrides}
      className="text-sm text-gray-500 hover:text-gray-700 underline"
    >
      Reset all to defaults
    </button>
  );
}

/**
 * Demo content with embedded values
 */
function DemoContent() {
  return (
    <div className="max-w-3xl">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">
        Great Aunt Moonbeam's Fairy Dust Farm
      </h1>
      <p className="text-lg text-gray-500 mb-6 italic">
        A Decision Analysis for the Enchanted Entrepreneur
      </p>

      <p className="text-gray-600 mb-8">
        You've inherited a fairy dust farm from your late Great Aunt Moonbeam.
        Should you keep it running or sell it and invest in the Wizard Index Fund?
        Click any underlined number to inspect it and adjust assumptions.
      </p>

      {/* Table of Contents */}
      <nav className="mb-10 p-4 bg-gray-50 border border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Contents</h2>
        <ul className="space-y-1 text-sm">
          <li><a href="#farm-overview" className="text-gray-600 hover:text-gray-900">Farm Overview</a></li>
          <li><a href="#dust-production" className="text-gray-600 hover:text-gray-900">Dust Production & Revenue</a></li>
          <li><a href="#expenses" className="text-gray-600 hover:text-gray-900">Operating Expenses</a></li>
          <li><a href="#projections" className="text-gray-600 hover:text-gray-900">10-Year Projections</a></li>
          <li><a href="#magical-constants" className="text-gray-600 hover:text-gray-900">Magical Constants</a></li>
        </ul>
      </nav>

      {/* Farm Overview */}
      <section id="farm-overview" className="mb-8 scroll-mt-16">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Farm Overview</h2>
        <p className="text-gray-700 mb-4">
          The farm is valued at <V id="farmValue" /> gold pieces according to the Gnome Appraisal Guild.
          Currently home to <V id="fairyCount" /> dust-producing fairies, it sits on prime enchanted meadowland
          near the Whispering Woods.
        </p>
        <p className="text-gray-700 mb-4">
          If you sold today, after <V id="farmSellingCosts" format="percent" decimals={0} /> gnome realtor fees,
          you'd walk away with <V id="initialSaleProceeds" /> gold pieces to invest elsewhere.
        </p>
      </section>

      {/* Dust Production */}
      <section id="dust-production" className="mb-8 scroll-mt-16">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Dust Production & Revenue</h2>
        <p className="text-gray-700 mb-4">
          Each fairy produces <V id="dustPerFairy" /> ounces of fairy dust per month.
          With <V id="fairyCount" /> fairies, that's <V id="monthlyDustProduction" /> ounces monthly,
          or <V id="annualDustProduction" /> ounces per year.
        </p>
        <p className="text-gray-700 mb-4">
          At the current market price of <V id="dustPrice" /> gold pieces per ounce,
          gross annual revenue is <V id="grossRevenue.year0" />.
        </p>

        <div className="bg-gray-50 border border-gray-200 p-4 mb-4">
          <h3 className="font-semibold text-gray-800 mb-3">Revenue After Deductions (Year 0)</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Gross Revenue</span>
              <span><V id="grossRevenue.year0" /></span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>− Troll Bridge Tolls (<V id="trollTollRate" format="percent" decimals={0} />)</span>
              <span className="text-gray-500">included below</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">After Tolls</span>
              <span><V id="revenueAfterTolls.year0" /></span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>− Wizard Guild Tax (<V id="wizardGuildTaxRate" format="percent" decimals={0} />)</span>
              <span className="text-gray-500">included below</span>
            </div>
            <div className="flex justify-between border-t border-gray-300 pt-2 font-medium">
              <span className="text-gray-800">Net Revenue After Taxes</span>
              <span><V id="revenueAfterTax.year0" /></span>
            </div>
          </div>
        </div>
      </section>

      {/* Operating Expenses */}
      <section id="expenses" className="mb-8 scroll-mt-16">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Operating Expenses</h2>
        <p className="text-gray-700 mb-4">
          Running a fairy farm requires careful attention to fairy wellbeing. Monthly expenses include:
        </p>

        <div className="bg-gray-50 border border-gray-200 p-4 mb-4">
          <h3 className="font-semibold text-gray-800 mb-3">Monthly Expense Breakdown</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Fairy Care (nectar & flowers)</span>
              <span><V id="monthlyFairyCare" /></span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Mushroom Housing Maintenance</span>
              <span><V id="housingCost" /></span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Sparkle Maintenance</span>
              <span><V id="sparkleMaintenanceCost" /></span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Elf Worker Wages (<V id="elfWorkers" /> elves)</span>
              <span><V id="monthlyElfLabor" /></span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>+ Insurance (prorated)</span>
              <span>~42 GP</span>
            </div>
            <div className="flex justify-between border-t border-gray-300 pt-2 font-medium">
              <span className="text-gray-800">Total Monthly</span>
              <span><V id="monthlyExpenses" /></span>
            </div>
            <div className="flex justify-between font-medium">
              <span className="text-gray-800">Total Annual</span>
              <span><V id="annualExpenses" /></span>
            </div>
          </div>
        </div>

        <p className="text-gray-700 mb-4">
          <strong>Year 0 Net Profit: <V id="netProfit.year0" /></strong>
        </p>
      </section>

      {/* 10-Year Projections */}
      <section id="projections" className="mb-8 scroll-mt-16">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">10-Year Projections</h2>

        <p className="text-gray-700 mb-4">
          With farm appreciation at <V id="farmAppreciation" format="percent" decimals={0} /> per year
          and dust prices growing at <V id="dustPriceGrowth" format="percent" decimals={0} />,
          let's see how the numbers stack up over a decade.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Keep Farm Scenario */}
          <div className="bg-gray-50 border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Keep the Farm</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Farm Value (Year 10)</span>
                <span><V id="farmValue.year10" /></span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">+ Reinvested Profits</span>
                <span><V id="investedProfits.year10" /></span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2">
                <span className="text-gray-700">Total Value</span>
                <span><V id="farmTotalValue" /></span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>− Exit Selling Costs</span>
                <span><V id="farmExitCosts" /></span>
              </div>
              <div className="flex justify-between border-t border-gray-300 pt-2 font-bold">
                <span className="text-gray-900">Net Liquidation</span>
                <span><V id="farmNetLiquidation" /></span>
              </div>
            </div>
          </div>

          {/* Sell & Invest Scenario */}
          <div className="bg-gray-50 border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Sell & Invest (60/40)</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Initial Proceeds</span>
                <span><V id="initialSaleProceeds" /></span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Wizard Index Return</span>
                <span><V id="wizardIndexReturns" format="percent" decimals={1} /></span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Dragon Bond Return</span>
                <span><V id="dragonBondReturns" format="percent" decimals={1} /></span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Blended Return</span>
                <span><V id="blendedReturn" format="percent" decimals={1} /></span>
              </div>
              <div className="flex justify-between border-t border-gray-300 pt-2 font-bold">
                <span className="text-gray-900">Portfolio Value</span>
                <span><V id="portfolioBlended.year10" /></span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-100 border border-gray-300 p-4 mb-6">
          <p className="text-lg text-gray-900">
            <strong>Bottom Line:</strong> Keeping the farm comes out{' '}
            <V id="farmAdvantage" />{' '}
            ahead of selling after 10 years.
          </p>
        </div>

        <p className="text-gray-700 mb-4">
          Over 10 years, you would produce a total of <V id="totalDustProduced" /> ounces
          of fairy dust—enough to supply the entire Potion Makers Guild for years!
        </p>

        <div className="bg-gray-50 border border-gray-200 p-4 mb-4">
          <h3 className="font-semibold text-gray-800 mb-3">Alternative Portfolio Outcomes (Year 10)</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">100% Wizard Index</span>
              <span><V id="portfolioWizard.year10" /></span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">60/40 Blended</span>
              <span><V id="portfolioBlended.year10" /></span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">100% Dragon Bonds</span>
              <span><V id="portfolioDragon.year10" /></span>
            </div>
          </div>
        </div>
      </section>

      {/* Magical Constants */}
      <section id="magical-constants" className="mb-8 scroll-mt-16">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Magical Constants</h2>
        <p className="text-gray-600 mb-4 text-sm">
          These are source values from official magical registries—click to see citations.
        </p>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Fairy Lifespan</span>
            <span><V id="fairyLifespan" /> years</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Wizard Guild Tax</span>
            <span><V id="wizardGuildTaxRate" format="percent" decimals={0} /></span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Elvish Minimum Wage</span>
            <span><V id="elvishMinimumWage" /> GP/day</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Troll Toll Rate</span>
            <span><V id="trollTollRate" format="percent" decimals={0} /></span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Dragon Bond Base Rate</span>
            <span><V id="dragonBondBaseRate" format="percent" decimals={1} /></span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Magical Liability Min.</span>
            <span><V id="magicalLiabilityMinimum" /> GP</span>
          </div>
        </div>
      </section>

      <div className="border-t border-gray-200 pt-4">
        <ResetButton />
      </div>
    </div>
  );
}

/**
 * Main demo component with provider
 */
export function FairyFarmDemo() {
  return (
    <GraphProvider definition={fairyDustFarmGraph} persistKey="fairy-farm-demo">
      <div className="min-h-screen bg-white pb-16">
        {/* Main content - centered with max width */}
        <div className="max-w-4xl mx-auto px-6 py-8 mr-80">
          <DemoContent />
        </div>

        {/* Inspector sidebar */}
        <InspectorPanel position="sidebar" width={400} />

        {/* Override banner - fixed at bottom */}
        <OverrideBanner />
      </div>
    </GraphProvider>
  );
}

export default FairyFarmDemo;
